import { SwaggerAudience } from '@activepieces/shared';
import { t } from 'i18next';
import { useId } from 'react';

import { ClientMultiCombobox, KnownClient } from './client-multi-combobox';

import { Label } from '@/components/ui/label';
import { SegmentedControl } from '@/components/ui/segmented-control';

type SwaggerAudienceFieldProps = {
  audience: SwaggerAudience;
  onAudienceChange: (audience: SwaggerAudience) => void;
  tenantIds: string[];
  onTenantIdsChange: (tenantIds: string[]) => void;
  /** Names and numbers of the already-assigned ids, for the picker to show. */
  knownClients?: Record<string, KnownClient>;
  disabled?: boolean;
};

/**
 * States the three cases apart instead of leaving "assigned to nobody" to be
 * inferred from an empty control — that case is the one an administrator is
 * most likely to misread, since a Swagger nobody can see looks exactly like a
 * Swagger that is merely not saved yet.
 */
const hintFor = (audience: SwaggerAudience, count: number): string => {
  if (audience === SwaggerAudience.ALL) {
    return t('Visible to all clients.');
  }
  if (count === 0) {
    return t('Not visible to any client yet. Select the ones who can see it.');
  }
  return t(
    'Only visible to the selected clients. Add or remove some as needed.',
  );
};

/**
 * Picks who a Swagger is exposed to: a set of clients, or every client at once.
 *
 * "Every client" is a distinct choice rather than a select-all over the client
 * list, because the two do not mean the same thing — a select-all freezes the
 * assignment at the clients that existed the day it was saved, and would miss
 * every client onboarded afterwards.
 */
const SwaggerAudienceField = ({
  audience,
  onAudienceChange,
  tenantIds,
  onTenantIdsChange,
  knownClients,
  disabled,
}: SwaggerAudienceFieldProps) => {
  const labelId = useId();

  /**
   * Switching mode empties the client list, in both directions. "Every client"
   * is not a superset of a selection — it is a different answer to the same
   * question — so a list left behind would come back the moment the mode is
   * switched back, silently reassigning clients the administrator had moved
   * away from. Cleared here rather than in each caller so the two screens that
   * use this field cannot disagree about it.
   */
  const changeAudience = (next: SwaggerAudience) => {
    if (next === audience) {
      return;
    }
    onAudienceChange(next);
    onTenantIdsChange([]);
  };

  return (
    <div className="flex flex-col gap-2">
      <Label id={labelId}>{t('Visibility')}</Label>
      <SegmentedControl
        aria-labelledby={labelId}
        size="thin"
        fullWidth
        value={audience}
        onValueChange={changeAudience}
        disabled={disabled}
        options={[
          {
            value: SwaggerAudience.ALL,
            label: t('All clients'),
          },
          {
            value: SwaggerAudience.CLIENTS,
            label: t('Selected clients'),
          },
        ]}
      />

      {audience === SwaggerAudience.CLIENTS && (
        <ClientMultiCombobox
          value={tenantIds}
          onChange={onTenantIdsChange}
          knownClients={knownClients}
          disabled={disabled}
        />
      )}

      <p className="text-xs text-muted-foreground">
        {hintFor(audience, tenantIds.length)}
      </p>
    </div>
  );
};

export { SwaggerAudienceField };
